/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';
import type {
  Skill_CareerRole as CareerRole,
  Specialization_Specialization as S12n,
} from '__generated__/graphql-types-do-not-use';

import type { TrackingData } from 'js/lib/retracked';

import { Button, breakpoints, typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { ExternalCoachAction } from 'bundles/ai-coach-platform/components/patterns/chat/ExternalCoachAction';
import { isIntentionRoutingEnabled } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { isPathwaysForAllEnabled } from 'bundles/page-config-common/utils/isPathwaysForAllEnabled';
import RecommendedPathwayEnrollButtonNonConfigurable from 'bundles/unified-common/components/recommended-pathway/RecommendedPathwayEnrollButtonNonConfigurable';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

import _t from 'i18n!nls/consumer-home';

const styles = {
  coachButton: css`
    &.pathways-recommendation-coach {
      span.cds-button-label {
        color: var(--cds-color-interactive-primary);
        ${typography2.actionSecondary};
      }

      &[aria-disabled='true'] {
        .cds-button-label {
          color: var(--cds-color-neutral-disabled);
        }
      }

      padding: var(--cds-spacing-100);
    }
  `,
  buttonOutline: css`
    ${breakpoints.down('sm')} {
      box-shadow: inset 0 0 0 1px var(--cds-color-interactive-primary);
      border-radius: var(--cds-spacing-50);
    }
  `,
  pathwaysForAllButtons: css`
    display: flex;
    gap: var(--cds-spacing-100);
  `,
};

type Props = {
  item: S12n;
  itemIndex: number;
  trackingData: TrackingData;
  pathways?: S12n[];
  role?: { name: string; slug: string };
  hideCoachButton?: boolean;
};

const isIntentRoutingEnabled = isIntentionRoutingEnabled();

const getCoachActionButtonConfig = (
  roleContext: { name: CareerRole['name']; slug: CareerRole['slug'] },
  item?: S12n,
  alternatives?: S12n[]
): AiCoachRespondToUserMessageInput => {
  return {
    courseraContext: {
      specializationId: item?.id,
      additionalProducts: alternatives?.map(({ id }) => ({
        productId: id,
        productType: 'SPECIALIZATION',
      })),
    },
    config: {
      coachModeId: isIntentRoutingEnabled ? 'PATHWAYS_LIHP' : 'PATHWAYS_MVP_TEST_MODE',
      coachActionId: 'HELP_ME_DECIDE_ACTION',
    },
    message: _t('Why is this recommended?'),
    additionalContext: [
      {
        contextKey: 'additionalContext.roleData',
        contextValue: `role-name: ${roleContext?.name}, role-slug: ${roleContext?.slug}.`,
      },
    ],
  };
};

/**
 * @description This component is used to render the buttons for the recommended pathway card
 */
const RecommendedPathwayButtons = (props: Props) => {
  const { item, itemIndex, trackingData, pathways, role, hideCoachButton = false } = props;
  const { setCoachOpen, setContextConfig } = useCoachState();
  const enablePathwaysForAll = isPathwaysForAllEnabled();
  const trackV3 = useTracker();

  const handleClick = () => {
    trackV3('click_button', {
      button: {
        name: 'program_info',
        linkName: 'pathway_module_xdp_link',
        linkURL: item.urlPath,
      },
      product: {
        id: item.id,
        slug: item.slug,
      },
      productCard: trackingData.productCard,
      carousel: trackingData.carousel,
    });
  };
  const coachActionButtonConfig = role && getCoachActionButtonConfig(role, item, pathways);

  return (
    <div css={enablePathwaysForAll ? styles.pathwaysForAllButtons : ''}>
      <RecommendedPathwayEnrollButtonNonConfigurable
        key="enroll"
        buttonProps={{ size: 'small' }}
        s12nId={item.id}
        courses={item.courses}
        partnerIds={item.partners?.map((partner) => partner.id)}
        trackingData={{
          button: {
            name: 'enroll',
          },
          product: {
            id: item.id,
            slug: item.slug,
          },
          productCard: trackingData.productCard,
          carousel: trackingData.carousel,
        }}
      />
      {!enablePathwaysForAll && (
        <Button
          component="a"
          key="details"
          size="small"
          variant="ghost"
          css={styles.buttonOutline}
          href={item.urlPath}
          onClick={handleClick}
          data-testid="view-details-button"
        >
          {_t('View details')}
        </Button>
      )}
      {!hideCoachButton && itemIndex === 0 && coachActionButtonConfig && (
        <ExternalCoachAction
          css={[styles.coachButton, styles.buttonOutline]}
          size="small"
          variant="ghost"
          newSessionOnClick={true}
          onClick={() => {
            const { message, ...coachChatContext } = coachActionButtonConfig;
            setContextConfig({
              ...coachChatContext,
              config: {
                coachModeId: isIntentRoutingEnabled ? 'PATHWAYS_LIHP' : 'PATHWAYS_MVP_TEST_MODE',
                coachActionId: isIntentRoutingEnabled ? 'ROUTER' : 'TYPED_MESSAGE',
              },
            });
            setCoachOpen(true);
            /*
              Note: ExternalCoachAction now provides click eventing by default,
              so this extra tracking is not needed. The schema being used here is incorrect,
              but since we have already launched with this eventing and have months
              of data in the dashboards, we want to keep sending this event for this
              particular button. Please reach out to the Coach platform team for any questions.
            */
            trackV3('view_coach', {
              product: { id: item.id, slug: item.slug },
              coachViewType: 'view_coach_launcher',
            });
          }}
          label={_t('Why is this recommended?')}
          {...coachActionButtonConfig}
          className="pathways-recommendation-coach"
          data-testid="why-is-this-recommended-button"
        />
      )}
    </div>
  );
};

export default RecommendedPathwayButtons;
